// Query builder compatible con PostgrestFilterBuilder de supabase-js.
//
// La aplicación tiene más de 700 llamadas del estilo
//   supabase.from('invoices').select('*').eq('user_id', id).order('date')
// repartidas por 40 archivos. En lugar de reescribirlas una a una, este builder
// mantiene exactamente la misma API: acumula la consulta y, al hacer `await`,
// la envía serializada al gateway, que la traduce a SQL sobre RDS.

import { GatewayError, request } from './transport';
import type { CountOption, PostgrestError, PostgrestResponse } from './types';

type Operation = 'select' | 'insert' | 'update' | 'upsert' | 'delete' | 'rpc';

interface ConditionFilter {
  type: 'condition';
  column: string;
  op: string;
  value: unknown;
  negate?: boolean;
}

interface LogicFilter {
  type: 'or' | 'and';
  expression: string;
}

type Filter = ConditionFilter | LogicFilter;

interface QueryRequest {
  table?: string;
  function?: string;
  args?: Record<string, unknown>;
  op: Operation;
  select: string;
  returning: boolean;
  values?: unknown;
  onConflict?: string;
  ignoreDuplicates?: boolean;
  filters: Filter[];
  order: Array<{ column: string; ascending: boolean; nullsFirst?: boolean }>;
  limit?: number;
  offset?: number;
  count?: CountOption | null;
  head?: boolean;
}

const toError = (err: unknown): PostgrestError => {
  if (err instanceof GatewayError) {
    return { message: err.message, details: err.details, hint: err.hint, code: err.code };
  }
  const message = err instanceof Error ? err.message : String(err);
  return { message, details: null, hint: null, code: 'unknown' };
};

export class QueryBuilder<T = any> implements PromiseLike<PostgrestResponse<T>> {
  private req: QueryRequest;
  private singleMode: 'single' | 'maybe' | null = null;
  private shouldThrow = false;
  private signal?: AbortSignal;

  constructor(init: Partial<QueryRequest> & { op: Operation }) {
    this.req = {
      select: '*',
      returning: false,
      filters: [],
      order: [],
      count: null,
      head: false,
      ...init,
    };
  }

  // ─── Selección y mutaciones ────────────────────────────────────────────────

  select(columns = '*', options?: { count?: CountOption; head?: boolean }): this {
    this.req.select = columns || '*';

    // En una mutación, `.select()` significa "devuélveme las filas afectadas"
    // (el RETURNING de SQL), no una consulta nueva.
    if (this.req.op !== 'select' && this.req.op !== 'rpc') {
      this.req.returning = true;
    }

    if (options?.count) this.req.count = options.count;
    if (options?.head) this.req.head = true;

    return this;
  }

  insert(values: unknown, options?: { count?: CountOption }): this {
    this.req.op = 'insert';
    this.req.values = values;
    this.req.returning = false;
    if (options?.count) this.req.count = options.count;
    return this;
  }

  upsert(
    values: unknown,
    options?: { onConflict?: string; ignoreDuplicates?: boolean; count?: CountOption },
  ): this {
    this.req.op = 'upsert';
    this.req.values = values;
    this.req.returning = false;
    this.req.onConflict = options?.onConflict;
    this.req.ignoreDuplicates = options?.ignoreDuplicates ?? false;
    if (options?.count) this.req.count = options.count;
    return this;
  }

  update(values: unknown, options?: { count?: CountOption }): this {
    this.req.op = 'update';
    this.req.values = values;
    this.req.returning = false;
    if (options?.count) this.req.count = options.count;
    return this;
  }

  delete(options?: { count?: CountOption }): this {
    this.req.op = 'delete';
    this.req.returning = false;
    if (options?.count) this.req.count = options.count;
    return this;
  }

  // ─── Filtros ───────────────────────────────────────────────────────────────

  private addFilter(column: string, op: string, value: unknown, negate = false): this {
    this.req.filters.push({ type: 'condition', column, op, value, negate });
    return this;
  }

  eq(column: string, value: unknown) { return this.addFilter(column, 'eq', value); }
  neq(column: string, value: unknown) { return this.addFilter(column, 'neq', value); }
  gt(column: string, value: unknown) { return this.addFilter(column, 'gt', value); }
  gte(column: string, value: unknown) { return this.addFilter(column, 'gte', value); }
  lt(column: string, value: unknown) { return this.addFilter(column, 'lt', value); }
  lte(column: string, value: unknown) { return this.addFilter(column, 'lte', value); }
  like(column: string, pattern: string) { return this.addFilter(column, 'like', pattern); }
  ilike(column: string, pattern: string) { return this.addFilter(column, 'ilike', pattern); }
  is(column: string, value: null | boolean) { return this.addFilter(column, 'is', value); }
  in(column: string, values: readonly unknown[]) { return this.addFilter(column, 'in', values); }
  contains(column: string, value: unknown) { return this.addFilter(column, 'contains', value); }
  containedBy(column: string, value: unknown) { return this.addFilter(column, 'containedby', value); }
  overlaps(column: string, value: unknown) { return this.addFilter(column, 'overlaps', value); }

  textSearch(column: string, query: string, options?: { type?: 'plain' | 'phrase' | 'websearch' }) {
    const op = options?.type === 'plain' ? 'plfts' : options?.type === 'websearch' ? 'wfts' : 'fts';
    return this.addFilter(column, op, query);
  }

  /** Azúcar de PostgREST: `.match({ a: 1, b: 2 })` equivale a dos `.eq()`. */
  match(query: Record<string, unknown>): this {
    for (const [column, value] of Object.entries(query)) {
      this.addFilter(column, 'eq', value);
    }
    return this;
  }

  not(column: string, op: string, value: unknown): this {
    return this.addFilter(column, String(op).toLowerCase(), value, true);
  }

  /** Filtro genérico: `.filter('status', 'in', '("a","b")')`. */
  filter(column: string, op: string, value: unknown): this {
    const operator = String(op).toLowerCase();
    if (operator.startsWith('not.')) {
      return this.addFilter(column, operator.slice(4), value, true);
    }
    return this.addFilter(column, operator, value);
  }

  /** Sintaxis PostgREST: `.or('a.eq.1,b.is.null')`. */
  or(expression: string, _options?: { foreignTable?: string; referencedTable?: string }): this {
    this.req.filters.push({ type: 'or', expression });
    return this;
  }

  // ─── Modificadores ─────────────────────────────────────────────────────────

  order(
    column: string,
    options?: { ascending?: boolean; nullsFirst?: boolean; foreignTable?: string; referencedTable?: string },
  ): this {
    this.req.order.push({
      column,
      ascending: options?.ascending !== false,
      nullsFirst: options?.nullsFirst,
    });
    return this;
  }

  limit(count: number): this {
    this.req.limit = count;
    return this;
  }

  range(from: number, to: number): this {
    this.req.offset = from;
    this.req.limit = to - from + 1;
    return this;
  }

  single(): this {
    this.singleMode = 'single';
    return this;
  }

  maybeSingle(): this {
    this.singleMode = 'maybe';
    return this;
  }

  throwOnError(): this {
    this.shouldThrow = true;
    return this;
  }

  abortSignal(signal: AbortSignal): this {
    this.signal = signal;
    return this;
  }

  // ─── Ejecución ─────────────────────────────────────────────────────────────

  private applySingle(rows: any[] | null, count: number | null, status: number): PostgrestResponse<T> {
    if (!this.singleMode) {
      return { data: rows as T, error: null, count, status, statusText: 'OK' };
    }

    const list = Array.isArray(rows) ? rows : rows == null ? [] : [rows];

    if (list.length === 1) {
      return { data: list[0] as T, error: null, count, status, statusText: 'OK' };
    }

    if (list.length === 0 && this.singleMode === 'maybe') {
      return { data: null, error: null, count, status, statusText: 'OK' };
    }

    // Mismos códigos y mensajes que PostgREST, porque hay código que los inspecciona.
    const error: PostgrestError =
      list.length === 0
        ? {
            code: 'PGRST116',
            message: 'JSON object requested, multiple (or no) rows returned',
            details: 'The result contains 0 rows',
            hint: null,
          }
        : {
            code: 'PGRST116',
            message: 'JSON object requested, multiple (or no) rows returned',
            details: `The result contains ${list.length} rows`,
            hint: null,
          };

    return { data: null, error, count, status: 406, statusText: 'Not Acceptable' };
  }

  /**
   * El resultado se memoriza: el builder es "thenable" y `then`/`catch`/`finally`
   * podrían dispararlo más de una vez sobre el mismo objeto.
   */
  execute(): Promise<PostgrestResponse<T>> {
    this.pending ??= this.run();
    return this.pending;
  }

  private pending?: Promise<PostgrestResponse<T>>;

  private async run(): Promise<PostgrestResponse<T>> {
    try {
      const payload = await request<{ data: any; count: number | null; status: number }>('/db/query', {
        method: 'POST',
        body: this.req,
        signal: this.signal,
      });

      const result = this.applySingle(payload.data, payload.count ?? null, payload.status ?? 200);

      if (this.shouldThrow && result.error) throw result.error;
      return result;
    } catch (err) {
      if ((err as any)?.name === 'AbortError') throw err;

      const error = toError(err);
      if (this.shouldThrow) throw err;

      return {
        data: null,
        error,
        count: null,
        status: err instanceof GatewayError ? err.status : 500,
        statusText: 'Error',
      };
    }
  }

  then<TResult1 = PostgrestResponse<T>, TResult2 = never>(
    onfulfilled?: ((value: PostgrestResponse<T>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }

  catch<TResult = never>(
    onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null,
  ): Promise<PostgrestResponse<T> | TResult> {
    return this.execute().catch(onrejected);
  }

  finally(onfinally?: (() => void) | null): Promise<PostgrestResponse<T>> {
    return this.execute().finally(onfinally);
  }
}

/** Equivalente a `supabase.from(tabla)`. */
export function from<T = any>(table: string): QueryBuilder<T> {
  return new QueryBuilder<T>({ op: 'select', table });
}

/** Equivalente a `supabase.rpc(nombre, argumentos)`. */
export function rpc<T = any>(fn: string, args: Record<string, unknown> = {}): QueryBuilder<T> {
  return new QueryBuilder<T>({ op: 'rpc', function: fn, args });
}
